import { useMemo, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import {
  ArrowDownLeft,
  ArrowLeft,
  ArrowUpRight,
  Building2,
  Clock3,
  Download,
  GitMerge,
  History,
  Mail,
  MoreHorizontal,
  Pencil,
  Search,
  ShieldCheck,
  Star,
  StickyNote,
  Tag,
  Trash2,
  Upload,
  UserPlus,
  Users,
} from 'lucide-react';
import type { Contact } from '@mailpilot/shared';
import { api, downloadFile } from '../lib/api';
import { qk } from '../lib/keys';
import { relativeTime, shortDate } from '../lib/format';
import { useBreakpoint, useDebounced } from '../hooks/useBrowser';
import {
  Avatar,
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  IconButton,
  Input,
  Menu,
  Page,
  PageHeader,
  Skeleton,
  SkeletonLines,
  useConfirm,
  useToast,
} from '../components/ui';
import { ContactFormModal } from '../components/contacts/ContactFormModal';
import { ContactSuggestions } from '../components/contacts/ContactSuggestions';
import { DuplicateFinder } from '../components/contacts/DuplicateFinder';
import { CsvImportModal } from '../components/contacts/CsvImportModal';

type ContactsList = { contacts: Contact[] };
type ContactDetail = { contact: Contact; history: { threadId: string; subject: string; date: string; direction: 'in' | 'out' }[] };

const label = (c: Contact) => c.name.trim() || c.emails[0] || 'Unnamed contact';

const sourceLabel: Record<Contact['source'], string> = {
  manual: 'Added manually',
  conversation: 'Saved from a conversation',
  csv: 'Imported from CSV',
  suggestion: 'Saved from suggestions',
  provider: 'Provider contacts',
};

/** Optimistic favourite toggle shared by list rows and the detail panel. */
function useToggleFavorite() {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: ({ id, favorite }: { id: string; favorite: boolean }) => api('PATCH /api/contacts/:id', { params: { id }, body: { favorite } }),
    onMutate: async ({ id, favorite }) => {
      await qc.cancelQueries({ queryKey: qk.contactsRoot });
      await qc.cancelQueries({ queryKey: qk.contact(id) });
      const lists = qc.getQueriesData<ContactsList>({ queryKey: qk.contactsRoot });
      const detail = qc.getQueryData<ContactDetail>(qk.contact(id));
      qc.setQueriesData<ContactsList>({ queryKey: qk.contactsRoot }, (old) => (old ? { contacts: old.contacts.map((c) => (c.id === id ? { ...c, favorite } : c)) } : old));
      if (detail) qc.setQueryData<ContactDetail>(qk.contact(id), { ...detail, contact: { ...detail.contact, favorite } });
      return { lists, detail };
    },
    onError: (e, { id }, ctx) => {
      ctx?.lists.forEach(([key, data]) => qc.setQueryData(key, data));
      if (ctx?.detail) qc.setQueryData(qk.contact(id), ctx.detail);
      toast.error(e);
    },
    onSettled: (_r, _e, { id }) => {
      void qc.invalidateQueries({ queryKey: qk.contactsRoot });
      void qc.invalidateQueries({ queryKey: qk.contact(id) });
    },
  });
}

export function ContactsPage() {
  const toast = useToast();
  const bp = useBreakpoint();
  const mobile = bp === 'mobile';
  const [params, setParams] = useSearchParams();
  const selectedId = params.get('id');
  const select = (id: string | null) =>
    setParams(
      (p) => {
        const n = new URLSearchParams(p);
        if (id) n.set('id', id);
        else n.delete('id');
        return n;
      },
      { replace: !mobile },
    );

  const [search, setSearch] = useState('');
  const q = useDebounced(search.trim(), 300);
  const [favOnly, setFavOnly] = useState(false);
  const [tag, setTag] = useState<string | null>(null);

  const [editing, setEditing] = useState<Contact | null>(null);
  const [creating, setCreating] = useState(false);
  const [dupOpen, setDupOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [exporting, setExporting] = useState(false);

  const query = { q: q || undefined, favorite: favOnly ? ('1' as const) : undefined };
  const list = useQuery({
    queryKey: qk.contacts({ q: query.q, favorite: query.favorite }),
    queryFn: () => api('GET /api/contacts', { query }),
    placeholderData: (prev) => prev,
  });
  const toggleFav = useToggleFavorite();

  const allTags = useMemo(() => {
    const s = new Set<string>();
    list.data?.contacts.forEach((c) => c.tags.forEach((t) => s.add(t)));
    if (tag) s.add(tag);
    return [...s].sort((a, b) => a.localeCompare(b));
  }, [list.data, tag]);
  const contacts = useMemo(() => (list.data?.contacts ?? []).filter((c) => !tag || c.tags.includes(tag)), [list.data, tag]);

  const filtered = !!q || favOnly || !!tag;

  const exportCsv = async () => {
    setExporting(true);
    try {
      await downloadFile('/api/contacts/export', 'contacts.csv');
    } catch (e) {
      toast.error(e);
    } finally {
      setExporting(false);
    }
  };

  const showList = !mobile || !selectedId;
  const showDetail = !mobile || !!selectedId;

  return (
    <Page wide>
      <PageHeader
        tone="sky"
        icon={<Users className="h-5 w-5" />}
        title="Contacts"
        description="Only the people you save or import — MailPilot never invents contact details."
        actions={
          <>
            <Menu
              label="More contact actions"
              align="right"
              trigger={(p) => <IconButton {...p} label="More contact actions" variant="secondary" icon={<MoreHorizontal className="h-[18px] w-[18px]" />} className="h-10 w-10" />}
              items={[
                { key: 'import', label: 'Import CSV', icon: <Upload className="h-4 w-4" />, onSelect: () => setImportOpen(true) },
                { key: 'export', label: exporting ? 'Exporting…' : 'Export CSV', icon: <Download className="h-4 w-4" />, onSelect: () => void exportCsv() },
                'divider',
                { key: 'dups', label: 'Find duplicates', icon: <GitMerge className="h-4 w-4" />, onSelect: () => setDupOpen(true) },
              ]}
            />
            <Button variant="primary" icon={<UserPlus className="h-4 w-4" />} onClick={() => setCreating(true)}>
              New contact
            </Button>
          </>
        }
      />

      <div className="space-y-5">
        {showList && <ContactSuggestions />}

        <div className="grid gap-5 md:grid-cols-[minmax(280px,360px)_1fr]">
          {showList && (
            <Card className="flex min-h-[460px] flex-col overflow-hidden">
              <div className="space-y-3 border-b border-line/60 p-4">
                <div className="relative">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" aria-hidden />
                  <Input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search contacts" aria-label="Search name, email, company or notes" className="bg-raised/60 pl-9" />
                </div>
                {(allTags.length > 0 || favOnly || (list.data?.contacts.length ?? 0) > 0) && (
                  <div className="-mx-1 flex flex-wrap items-center gap-0.5" role="group" aria-label="Filters">
                    <FilterChip active={favOnly} onClick={() => setFavOnly((v) => !v)}>
                      <Star className={clsx('h-3.5 w-3.5', favOnly && 'fill-current')} aria-hidden /> Favourites
                    </FilterChip>
                    {allTags.map((t) => (
                      <FilterChip key={t} active={tag === t} onClick={() => setTag((cur) => (cur === t ? null : t))}>
                        <Tag className="h-3.5 w-3.5" aria-hidden /> {t}
                      </FilterChip>
                    ))}
                  </div>
                )}
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto p-2 scroll-thin md:max-h-[calc(100dvh-300px)]" aria-busy={list.isFetching || undefined}>
                {list.isLoading ? (
                  <div className="space-y-3 p-2">
                    {[0, 1, 2, 3, 4].map((i) => (
                      <div key={i} className="flex items-center gap-3">
                        <Skeleton className="h-10 w-10 rounded-full" />
                        <SkeletonLines lines={2} className="flex-1" />
                      </div>
                    ))}
                  </div>
                ) : list.isError ? (
                  <ErrorState compact error={list.error} onRetry={() => void list.refetch()} />
                ) : contacts.length === 0 ? (
                  filtered ? (
                    <EmptyState
                      icon={<Search className="h-6 w-6" />}
                      title="No matching contacts"
                      description="Try a different search or clear the filters."
                      actions={
                        <Button
                          size="sm"
                          onClick={() => {
                            setSearch('');
                            setFavOnly(false);
                            setTag(null);
                          }}
                        >
                          Clear filters
                        </Button>
                      }
                    />
                  ) : (
                    <EmptyState
                      icon={<Users className="h-6 w-6" />}
                      title="No contacts yet"
                      description="Add people yourself, import a CSV, or save frequent correspondents from the suggestions above."
                      actions={
                        <>
                          <Button variant="primary" size="sm" icon={<UserPlus className="h-4 w-4" />} onClick={() => setCreating(true)}>
                            New contact
                          </Button>
                          <Button size="sm" icon={<Upload className="h-4 w-4" />} onClick={() => setImportOpen(true)}>
                            Import CSV
                          </Button>
                        </>
                      }
                    />
                  )
                ) : (
                  <ul aria-label="Contacts" className="space-y-0.5">
                    {contacts.map((c) => (
                      <ContactRow
                        key={c.id}
                        contact={c}
                        selected={c.id === selectedId}
                        onSelect={() => select(c.id)}
                        onToggleFav={() => toggleFav.mutate({ id: c.id, favorite: !c.favorite })}
                      />
                    ))}
                  </ul>
                )}
              </div>
              {contacts.length > 0 && (
                <p className="border-t border-line/60 px-4 py-2 text-[0.6875rem] font-medium uppercase tracking-wider text-subtle">
                  {contacts.length} {contacts.length === 1 ? 'contact' : 'contacts'}
                </p>
              )}
            </Card>
          )}

          {showDetail && (
            <div className="min-w-0">
              {selectedId ? (
                <ContactDetailPanel
                  id={selectedId}
                  mobile={mobile}
                  onBack={() => select(null)}
                  onEdit={setEditing}
                  onDeleted={() => select(null)}
                  onToggleFav={(c) => toggleFav.mutate({ id: c.id, favorite: !c.favorite })}
                />
              ) : (
                <Card className="hidden h-full min-h-[460px] items-center justify-center md:flex">
                  <EmptyState
                    icon={<Users className="h-6 w-6" />}
                    title="Select a contact"
                    description="Choose someone from the list to see their details and conversation history."
                    actions={
                      <Button size="sm" variant="soft" icon={<UserPlus className="h-4 w-4" />} onClick={() => setCreating(true)}>
                        New contact
                      </Button>
                    }
                  />
                </Card>
              )}
            </div>
          )}
        </div>
      </div>

      <ContactFormModal open={creating} onClose={() => setCreating(false)} contact={null} onSaved={(c) => select(c.id)} />
      <ContactFormModal open={!!editing} onClose={() => setEditing(null)} contact={editing} />
      <DuplicateFinder open={dupOpen} onClose={() => setDupOpen(false)} />
      <CsvImportModal open={importOpen} onClose={() => setImportOpen(false)} />
    </Page>
  );
}

function FilterChip({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button type="button" aria-pressed={active} onClick={onClick} className="mp-chip !h-8">
      {children}
    </button>
  );
}

function ContactRow({ contact: c, selected, onSelect, onToggleFav }: { contact: Contact; selected: boolean; onSelect: () => void; onToggleFav: () => void }) {
  return (
    <li className={clsx('group flex items-center gap-1 rounded-xl pr-1.5 transition-colors', selected ? 'bg-primary-soft' : 'hover:bg-raised')}>
      <button type="button" onClick={onSelect} aria-current={selected || undefined} className="flex min-h-[56px] min-w-0 flex-1 items-center gap-3 rounded-xl py-2 pl-2.5 text-left">
        <Avatar name={c.name} email={c.emails[0] ?? c.id} size={38} />
        <span className="min-w-0 flex-1">
          <span className={clsx('block truncate text-[0.875rem] font-medium', selected ? 'text-primary-ink' : 'text-ink')}>{label(c)}</span>
          <span className="block truncate text-xs text-muted">
            {c.emails[0] ?? 'No email'}
            {c.company ? ` · ${c.company}` : ''}
          </span>
          {c.tags.length > 0 && (
            <span className="mt-1 flex flex-wrap gap-1">
              {c.tags.slice(0, 3).map((t) => (
                <Badge key={t} className="!py-0 !text-[0.625rem]">{t}</Badge>
              ))}
              {c.tags.length > 3 && <Badge className="!py-0 !text-[0.625rem]">+{c.tags.length - 3}</Badge>}
            </span>
          )}
        </span>
      </button>
      <IconButton
        size="sm"
        label={c.favorite ? `Remove ${label(c)} from favourites` : `Add ${label(c)} to favourites`}
        aria-pressed={c.favorite}
        onClick={onToggleFav}
        icon={<Star className={clsx('h-4 w-4', c.favorite ? 'fill-warn text-warn' : '')} />}
        className={clsx('transition-opacity', !c.favorite && 'opacity-0 focus:opacity-100 group-hover:opacity-100')}
      />
    </li>
  );
}

function DetailField({ icon, label: fieldLabel, children, className }: { icon: ReactNode; label: string; children: ReactNode; className?: string }) {
  return (
    <div className={className}>
      <dt className="flex items-center gap-1.5 text-[0.6875rem] font-semibold uppercase tracking-wider text-subtle">
        {icon}
        {fieldLabel}
      </dt>
      <dd className="mt-1.5 text-[0.875rem] text-ink">{children}</dd>
    </div>
  );
}

function ContactDetailPanel({
  id,
  mobile,
  onBack,
  onEdit,
  onDeleted,
  onToggleFav,
}: {
  id: string;
  mobile: boolean;
  onBack: () => void;
  onEdit: (c: Contact) => void;
  onDeleted: () => void;
  onToggleFav: (c: Contact) => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const d = useQuery({ queryKey: qk.contact(id), queryFn: () => api('GET /api/contacts/:id', { params: { id } }) });

  const del = useMutation({
    mutationFn: () => api('DELETE /api/contacts/:id', { params: { id } }),
    onSuccess: () => {
      toast.success('Contact deleted');
      qc.removeQueries({ queryKey: qk.contact(id) });
      void qc.invalidateQueries({ queryKey: qk.contactsRoot });
      void qc.invalidateQueries({ queryKey: qk.contactDuplicates });
      void qc.invalidateQueries({ queryKey: qk.contactSuggestions });
      onDeleted();
    },
    onError: (e) => toast.error(e),
  });

  const back = mobile && (
    <Button variant="ghost" icon={<ArrowLeft className="h-4 w-4" />} onClick={onBack} className="mb-3">
      All contacts
    </Button>
  );

  if (d.isLoading) {
    return (
      <div>
        {back}
        <Card className="space-y-4 p-6">
          <div className="flex items-center gap-4">
            <Skeleton className="h-16 w-16 rounded-full" />
            <SkeletonLines lines={2} className="flex-1" />
          </div>
          <SkeletonLines lines={5} />
        </Card>
      </div>
    );
  }
  if (d.isError || !d.data) {
    return (
      <div>
        {back}
        <Card>
          <ErrorState error={d.error} onRetry={() => void d.refetch()} title="Couldn't load this contact" />
        </Card>
      </div>
    );
  }

  const { contact: c, history } = d.data;

  return (
    <div className="space-y-5">
      {back}
      <Card>
        <div className="flex flex-wrap items-start gap-4 p-6">
          <Avatar name={c.name} email={c.emails[0] ?? c.id} size={64} />
          <div className="min-w-0 flex-1">
            <h2 className="flex items-center gap-2 text-xl font-semibold text-ink">
              <span className="truncate">{label(c)}</span>
              {c.favorite && <Star className="h-4 w-4 shrink-0 fill-warn text-warn" aria-label="Favourite" />}
            </h2>
            {c.company && (
              <p className="mt-0.5 flex items-center gap-1.5 text-[0.8125rem] text-muted">
                <Building2 className="h-4 w-4" aria-hidden /> {c.company}
              </p>
            )}
            {c.tags.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1">
                {c.tags.map((t) => (
                  <Badge key={t} tone="primary" icon={<Tag className="h-3 w-3" aria-hidden />}>
                    {t}
                  </Badge>
                ))}
              </div>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <IconButton
              label={c.favorite ? 'Remove from favourites' : 'Add to favourites'}
              aria-pressed={c.favorite}
              variant="secondary"
              onClick={() => onToggleFav(c)}
              icon={<Star className={clsx('h-4 w-4', c.favorite && 'fill-warn text-warn')} />}
            />
            <Button icon={<Pencil className="h-4 w-4" />} onClick={() => onEdit(c)}>
              Edit
            </Button>
            <Button
              variant="ghost"
              className="text-danger-ink hover:bg-danger-soft"
              icon={<Trash2 className="h-4 w-4" />}
              loading={del.isPending}
              onClick={async () => {
                const ok = await confirm({
                  title: `Delete ${label(c)}?`,
                  message: 'The contact is removed from all your devices. Your emails with them are not affected.',
                  confirmLabel: 'Delete contact',
                  danger: true,
                });
                if (ok) del.mutate();
              }}
            >
              Delete
            </Button>
          </div>
        </div>

        <dl className="grid gap-5 border-t border-line/60 px-6 py-5 sm:grid-cols-2">
          <DetailField icon={<Mail className="h-3.5 w-3.5" aria-hidden />} label="Email" className="sm:col-span-2">
            {c.emails.length ? (
              <div className="flex flex-wrap gap-2">
                {c.emails.map((e) => (
                  <a key={e} href={`mailto:${e}`} className="inline-flex items-center gap-1.5 rounded-lg bg-raised px-2.5 py-1 text-[0.8125rem] font-medium text-primary-ink transition-colors hover:bg-primary-soft">
                    {e}
                  </a>
                ))}
              </div>
            ) : (
              <span className="text-muted">No email address</span>
            )}
          </DetailField>
          <DetailField icon={<Clock3 className="h-3.5 w-3.5" aria-hidden />} label="Interactions">
            {c.interactionCount} {c.interactionCount === 1 ? 'message' : 'messages'}
            {c.lastInteractionAt && <span className="text-muted"> · last {relativeTime(c.lastInteractionAt)}</span>}
          </DetailField>
          <DetailField icon={<ShieldCheck className="h-3.5 w-3.5" aria-hidden />} label="Source">
            {sourceLabel[c.source]} <span className="text-muted">· {shortDate(c.createdAt)}</span>
          </DetailField>
          <DetailField icon={<StickyNote className="h-3.5 w-3.5" aria-hidden />} label="Notes" className="sm:col-span-2">
            <span className="whitespace-pre-wrap leading-relaxed">{c.notes.trim() ? c.notes : <span className="text-muted">No notes</span>}</span>
          </DetailField>
        </dl>
        <p className="flex items-center gap-1.5 border-t border-line/60 px-6 py-3 text-xs text-muted">
          <ShieldCheck className="h-3.5 w-3.5" aria-hidden /> Only details you saved or imported are shown.
        </p>
      </Card>

      <Card>
        <div className="flex items-center gap-2 px-6 pt-5">
          <History className="h-4 w-4 text-muted" aria-hidden />
          <h3 className="text-[0.9375rem] font-semibold text-ink">Interaction history</h3>
          {history.length > 0 && <span className="rounded-full bg-raised px-2 text-[0.6875rem] font-semibold leading-5 text-muted">{history.length}</span>}
        </div>
        {history.length === 0 ? (
          <p className="px-6 pb-6 pt-2 text-[0.8125rem] text-muted">No conversations with this contact in your imported mail yet.</p>
        ) : (
          <ul className="p-3 pt-3">
            {history.map((h) => (
              <li key={`${h.threadId}-${h.date}`}>
                <Link to={`/inbox/${h.threadId}`} className="group flex min-h-11 items-center gap-3 rounded-xl px-3 py-2 transition-colors hover:bg-raised">
                  <span
                    className={clsx('flex h-8 w-8 shrink-0 items-center justify-center rounded-lg', h.direction === 'in' ? 'bg-info-soft text-info-ink' : 'bg-success-soft text-success-ink')}
                    title={h.direction === 'in' ? 'Received' : 'Sent'}
                  >
                    {h.direction === 'in' ? <ArrowDownLeft className="h-4 w-4" aria-hidden /> : <ArrowUpRight className="h-4 w-4" aria-hidden />}
                    <span className="sr-only">{h.direction === 'in' ? 'Received' : 'Sent'}</span>
                  </span>
                  <span className="min-w-0 flex-1 truncate text-[0.875rem] text-ink">{h.subject || '(no subject)'}</span>
                  <span className="shrink-0 text-xs text-muted">{relativeTime(h.date)}</span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
